import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Trophy,
  TrendDown,
  TrendUp,
  MinusCircle,
  Info,
  Warning,
  CaretDown,
} from "@phosphor-icons/react";

/**
 * Performance vs marché — V4.14.
 *
 * Affiche les 4 délais clés depuis date de compromis comparés au benchmark
 * marché paramétrable. Vert si plus rapide que le marché, rouge sinon.
 *
 * Doctrine V4.14 : transparence statistique AVANT support commercial externe.
 *   - Échantillon par étape exposé clairement
 *   - Période de référence affichée (12 derniers mois par défaut)
 *   - Définition exacte de chaque délai accessible via icône info
 *   - Warning visuel si échantillon < seuil de fiabilité
 *
 * Safe = Silence : si aucun dossier signé avec compromis dans la période,
 * le bloc ne s'affiche pas (pas de message vide).
 */

const BENCHMARK_STAGES = ["envoi_banque", "accord_banque", "offre_editee", "acte_authentique"];
const BENCHMARK_LABELS = {
  envoi_banque: "Dépôt banque",
  accord_banque: "Accord banque",
  offre_editee: "Offre de prêt",
  acte_authentique: "Acte authentique",
};

// Seuil minimum d'échantillon pour considérer une stat "défendable" en externe
const MIN_RELIABLE_SAMPLE = 5;

function formatPeriodStart(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function BenchmarkRow({ stage, expanded, onToggle }) {
  const hasData = stage.avg_days !== null;
  const delta = stage.delta_vs_objective;
  const isFaster = hasData && delta < 0;
  const isSlower = hasData && delta > 0;
  const tone = !hasData ? "muted" : isFaster ? "good" : isSlower ? "bad" : "neutral";

  const colors = {
    good: { fg: "var(--cf-success-text)", bg: "var(--cf-success-soft)", Icon: TrendDown },
    bad: { fg: "var(--cf-danger-text)", bg: "var(--cf-danger-soft)", Icon: TrendUp },
    neutral: { fg: "var(--cf-primary-hover)", bg: "var(--cf-primary-soft)", Icon: MinusCircle },
    muted: { fg: "var(--cf-text-3)", bg: "var(--cf-bg-2)", Icon: MinusCircle },
  }[tone];

  const ecartLabel = !hasData
    ? "—"
    : isFaster
    ? `${Math.abs(delta).toFixed(0)}j d'avance`
    : isSlower
    ? `${delta.toFixed(0)}j de retard`
    : "Au niveau";

  const sampleThin = hasData && stage.sample_size < MIN_RELIABLE_SAMPLE;

  return (
    <li className="border-b border-slate-100 last:border-0 py-2" data-testid={`bench-row-${stage.key}`}>
      <div className="flex items-center gap-3">
        {/* Libellé + sample + bouton info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <div className="font-bold text-[13.5px] text-slate-900 truncate">{stage.label}</div>
            <button
              type="button"
              onClick={onToggle}
              className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
              aria-label="Définition du délai"
              data-testid={`bench-info-${stage.key}`}
            >
              <Info size={13} weight={expanded ? "fill" : "regular"} />
            </button>
            {sampleThin && (
              <span
                className="inline-flex items-center gap-0.5 text-[9.5px] font-extrabold uppercase tracking-wide text-[color:var(--cf-warning-text)] bg-[color:var(--cf-warning-soft)] px-1.5 py-0.5 rounded"
                title="Échantillon faible, à interpréter avec prudence"
                data-testid={`bench-warn-${stage.key}`}
              >
                <Warning size={9} weight="fill" /> Données fines
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-400 font-semibold">
            {hasData ? `Échantillon : ${stage.sample_size} dossier${stage.sample_size > 1 ? "s" : ""}` : "Pas encore de mesure"}
          </div>
        </div>

        {/* Délai réel */}
        <div className="text-right tabular-nums flex-shrink-0 w-14">
          <div className="font-extrabold text-[15px]" style={{ color: hasData ? "var(--cf-text)" : "var(--cf-text-3)" }}>
            {hasData ? `${Math.round(stage.avg_days)}j` : "—"}
          </div>
          <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">vous</div>
        </div>

        {/* Benchmark marché */}
        <div className="text-right tabular-nums flex-shrink-0 w-14">
          <div className="font-extrabold text-[15px] text-slate-500">{stage.objective_days}j</div>
          <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">marché</div>
        </div>

        {/* Pill écart */}
        <div
          className="px-2 py-1 rounded-lg text-[11px] font-extrabold tabular-nums flex-shrink-0 flex items-center gap-1 min-w-[88px] justify-center"
          style={{ background: colors.bg, color: colors.fg }}
          data-testid={`bench-delta-${stage.key}`}
        >
          <colors.Icon size={12} weight="bold" />
          <span>{ecartLabel}</span>
        </div>
      </div>

      {/* Définition dépliable */}
      {expanded && (
        <div
          className="mt-2 ml-0 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-[11.5px] leading-relaxed text-slate-600"
          data-testid={`bench-definition-${stage.key}`}
        >
          {stage.definition}
        </div>
      )}
    </li>
  );
}

export default function BenchmarkMarche() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [showMethodology, setShowMethodology] = useState(false);

  useEffect(() => {
    api
      .get("/performance/operational?period_months=12")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data || !data.dossiers_with_compromis) return null;

  // On ne garde que les 4 étapes benchmarkées
  const rows = (data.stages || [])
    .filter((s) => BENCHMARK_STAGES.includes(s.key))
    .map((s) => ({ ...s, label: BENCHMARK_LABELS[s.key] || s.label }));

  const hasAnyData = rows.some((s) => s.avg_days !== null);
  if (!hasAnyData) return null;

  const totalSample = data.dossiers_with_compromis;
  const periodLabel = data.period_label || "12 derniers mois";
  const periodStartFmt = formatPeriodStart(data.period_start);
  const globallyThin = totalSample < MIN_RELIABLE_SAMPLE;

  return (
    <section className="card-base p-5 mb-4" data-testid="benchmark-marche-card">
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={18} weight="fill" color="var(--cf-info)" />
        <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">
          Performance vs marché
        </h2>
      </div>

      {/* Bandeau transparence : période + échantillon */}
      <div className="text-[12px] text-slate-500 font-semibold mb-3 leading-snug" data-testid="benchmark-period-label">
        Calculé sur <span className="font-extrabold text-slate-700">{totalSample} dossier{totalSample > 1 ? "s" : ""} signé{totalSample > 1 ? "s" : ""}</span>
        {" · "}
        <span className="font-extrabold text-slate-700">{periodLabel}</span>
        {periodStartFmt !== "—" && (
          <span className="text-slate-400"> (depuis {periodStartFmt})</span>
        )}
      </div>

      {/* Warning global si échantillon insuffisant */}
      {globallyThin && (
        <div
          className="mb-3 px-3 py-2 rounded-lg bg-[color:var(--cf-warning-soft)] border border-[color:var(--cf-warning-border)] text-[11.5px] font-semibold text-[color:var(--cf-warning-text)] flex items-start gap-2"
          data-testid="benchmark-warning-thin"
        >
          <Warning size={14} weight="fill" color="var(--cf-warning)" className="flex-shrink-0 mt-0.5" />
          <span>
            Échantillon &lt; {MIN_RELIABLE_SAMPLE} dossiers — usage <strong>interne</strong> uniquement.
            Pas encore défendable comme preuve commerciale auprès des agents.
          </span>
        </div>
      )}

      {/* Liste des 4 étapes */}
      <ul className="space-y-0">
        {rows.map((s) => (
          <BenchmarkRow
            key={s.key}
            stage={s}
            expanded={expandedKey === s.key}
            onToggle={() => setExpandedKey(expandedKey === s.key ? null : s.key)}
          />
        ))}
      </ul>

      {/* Méthodologie dépliable */}
      <button
        type="button"
        onClick={() => setShowMethodology(!showMethodology)}
        className="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-slate-700 transition-colors"
        data-testid="benchmark-methodology-toggle"
      >
        <CaretDown
          size={11}
          weight="bold"
          className={`transition-transform ${showMethodology ? "rotate-180" : ""}`}
        />
        Méthodologie
      </button>
      {showMethodology && (
        <div
          className="mt-2 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-[11.5px] leading-relaxed text-slate-600 space-y-1.5"
          data-testid="benchmark-methodology-content"
        >
          <p>
            <strong>Période</strong> : {periodLabel} (à partir du {periodStartFmt}).
          </p>
          <p>
            <strong>Source</strong> : vos dossiers signés (acte authentique) ayant une date de compromis renseignée.
          </p>
          <p>
            <strong>Calcul</strong> : pour chaque étape, moyenne des écarts (en jours
            calendaires) entre la date de compromis et la date réelle de l'étape, mesurée
            sur vos dossiers personnels.
          </p>
          <p>
            <strong>Benchmark marché</strong> : valeurs de référence paramétrables
            (défauts 30j / 40j / 60j / 90j). Données indicatives basées sur des standards courants.
          </p>
          <p className="text-slate-400 italic">
            Pour un usage commercial externe, échantillon recommandé : ≥ {MIN_RELIABLE_SAMPLE} dossiers par étape.
          </p>
        </div>
      )}
    </section>
  );
}
